import { refreshAccessToken } from "@/features/auth/authApi"
import { dispatchRefreshTokenRevoked } from "@/features/auth/refreshTokenRevoked"
import {
  isSessionExpiredError,
  readOAuthErrorCode,
  SessionExpiredError,
} from "@/features/auth/sessionExpired"
import { tokenStorage } from "@/features/auth/tokenStorage"
import type { TokenResponse } from "@/lib/api/http"
import { ApiError } from "@/lib/api/http"

type SetSession = (tokens: TokenResponse) => void

const awaitSessionExpiredRedirect = <T>() => new Promise<T>(() => {})

const shouldTreatAsSessionExpired = (error: unknown) => {
  if (isSessionExpiredError(error)) return true
  if (error instanceof ApiError && readOAuthErrorCode(error.body) === "invalid_grant") return true
  if (error instanceof ApiError && error.status === 401 && !tokenStorage.getRefreshToken()) return true
  return false
}

/** Один повтор запроса после refresh при 401 */
export const withAccessTokenRetry = async <T>(
  accessToken: string | null,
  setSession: SetSession,
  request: (token: string) => Promise<T>,
): Promise<T> => {
  if (!accessToken) {
    throw new ApiError("Требуется вход", 401, null)
  }
  try {
    return await request(accessToken)
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) {
      throw e
    }
    const refresh = tokenStorage.getRefreshToken()
    if (!refresh) {
      tokenStorage.clear()
      dispatchRefreshTokenRevoked()
      return awaitSessionExpiredRedirect<T>()
    }
    try {
      const tokens = await refreshAccessToken(refresh)
      setSession(tokens)
      return await request(tokens.access_token)
    } catch (refreshError) {
      if (shouldTreatAsSessionExpired(refreshError)) {
        if (!isSessionExpiredError(refreshError)) {
          tokenStorage.clear()
          dispatchRefreshTokenRevoked()
        }
        return awaitSessionExpiredRedirect<T>()
      }
      throw refreshError
    }
  }
}
